import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FiUser, FiCreditCard, FiDownload, FiEdit2, FiVideo,
  FiCheckCircle, FiPlay, FiBook, FiArrowRight, FiAward,
  FiClock, FiTrendingUp
} from 'react-icons/fi';
import {
  getMyPayments, getMyApplications,
  updateProfile, getMyEnrollments,  getLiveClasses
} from '../utils/api';
import { useAuth } from '../context/AuthContext';



const statusBadge = (status) => {
  const map = {
    pending:['badge-warning','⏳ Pending'], under_review:['badge-primary','🔍 Under Review'],
    approved:['badge-success','✅ Approved'], rejected:['badge-error','❌ Rejected'], enrolled:['badge-success','🎓 Enrolled'],
  };
  const [cls,label] = map[status]||['badge-dark',status];
  return <span className={`badge ${cls}`}>{label}</span>;
};
const payBadge = (status) => {
  const map = { paid:['badge-success','✅ Paid'], created:['badge-warning','⏳ Pending'], failed:['badge-error','❌ Failed'] };
  const [cls,label] = map[status]||['badge-dark',status];
  return <span className={`badge ${cls}`}>{label}</span>;
};

const ProgressRing = ({ pct=0, size=56 }) => {
  const r=(size-8)/2, circ=2*Math.PI*r, dash=(pct/100)*circ;
  return (
    <svg width={size} height={size} style={{flexShrink:0}}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#e2e8f0" strokeWidth={6}/>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--primary)" strokeWidth={6}
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
        style={{transformOrigin:'50% 50%',transform:'rotate(-90deg)',transition:'stroke-dasharray 0.6s ease'}}/>
      <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle"
        style={{fontSize:size<50?9:12,fontWeight:700,fill:'var(--dark)',fontFamily:'var(--font-display)'}}>
        {pct}%
      </text>
    </svg>
  );
};

const EnrolledCourseCard = ({ enrollment }) => {
  const course = enrollment.course||{};
  const visItems = enrollment.modules?.flatMap(m=>m.items?.filter(i=>i.isVisible)||[])||[];
  const totalItems = visItems.length;
  const doneItems  = enrollment.completedItems?.length||0;
  const pct = enrollment.progressPercent||0;
  const catIcon = {Development:'💻','Data Science':'🤖',Marketing:'📱'}[course.category]||'📚';
  const catColor = {Development:'#dbeafe','Data Science':'#f3e8ff',Marketing:'#fef3c7'}[course.category]||'#f0f4ff';
  const counts = {video:0,file:0,assignment:0,note:0,link:0};
  enrollment.modules?.forEach(m=>m.items?.filter(i=>i.isVisible).forEach(i=>{counts[i.type]=(counts[i.type]||0)+1;}));
  const chips = [
    counts.video&&{label:`▶ ${counts.video} Video${counts.video>1?'s':''}`,bg:'#dbeafe',color:'#1d4ed8'},
    counts.file&&{label:`📄 ${counts.file} File${counts.file>1?'s':''}`,bg:'#f3e8ff',color:'#7c3aed'},
    counts.assignment&&{label:`📝 ${counts.assignment} Assignment${counts.assignment>1?'s':''}`,bg:'#fef3c7',color:'#b45309'},
    counts.note&&{label:`📌 ${counts.note} Note${counts.note>1?'s':''}`,bg:'#fee2e2',color:'#dc2626'},
    counts.link&&{label:`🔗 ${counts.link} Link${counts.link>1?'s':''}`,bg:'#d1fae5',color:'#059669'},
  ].filter(Boolean);

  return (
    <div style={{background:'white',border:'1px solid var(--border)',borderRadius:'var(--radius-xl)',overflow:'hidden',boxShadow:'var(--shadow-sm)',transition:'var(--transition)',display:'flex',flexDirection:'column'}}
      onMouseOver={e=>e.currentTarget.style.boxShadow='var(--shadow-md)'}
      onMouseOut={e=>e.currentTarget.style.boxShadow='var(--shadow-sm)'}
    >
      <div style={{height:4,background:'linear-gradient(90deg,var(--primary),var(--accent))'}}/>
      <div style={{padding:'20px 22px',flex:1,display:'flex',flexDirection:'column'}}>
        <div style={{display:'flex',gap:14,alignItems:'flex-start',marginBottom:14}}>
          <div style={{width:50,height:50,borderRadius:14,background:catColor,display:'flex',alignItems:'center',justifyContent:'center',fontSize:26,flexShrink:0}}>{catIcon}</div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontWeight:700,fontSize:15,lineHeight:1.3,marginBottom:4}}>{course.title}</div>
            <div style={{fontSize:12,color:'var(--mid)',display:'flex',gap:10,flexWrap:'wrap'}}>
              <span><FiClock size={11} style={{marginRight:3}}/>{course.duration}</span>
              {enrollment.batchName&&<span style={{color:'var(--primary)',fontWeight:600}}>📅 {enrollment.batchName}</span>}
            </div>
          </div>
          <ProgressRing pct={pct} size={52}/>
        </div>
        <div style={{height:6,background:'var(--border)',borderRadius:99,marginBottom:6,overflow:'hidden'}}>
          <div style={{height:'100%',borderRadius:99,background:pct===100?'linear-gradient(90deg,var(--success),#10b981)':'linear-gradient(90deg,var(--primary),var(--accent))',width:`${pct}%`,transition:'width 0.6s ease'}}/>
        </div>
        <div style={{display:'flex',justifyContent:'space-between',fontSize:11,color:'var(--mid)',marginBottom:14}}>
          <span>{doneItems}/{totalItems} items completed</span><span>{pct}% done</span>
        </div>
        {chips.length>0?(
          <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:14}}>
            {chips.map((c,i)=><span key={i} style={{background:c.bg,color:c.color,fontSize:11,fontWeight:600,padding:'3px 9px',borderRadius:999}}>{c.label}</span>)}
          </div>
        ):(
          <div style={{background:'var(--bg)',border:'1px dashed var(--border)',borderRadius:'var(--radius)',padding:'10px 14px',textAlign:'center',color:'var(--mid)',fontSize:13,marginBottom:14}}>
            Content being prepared by your instructor…
          </div>
        )}
        {enrollment.studentNote&&(
          <div style={{background:'#fffbeb',border:'1px solid #fde68a',borderRadius:'var(--radius)',padding:'8px 12px',marginBottom:14,fontSize:12,color:'#92400e'}}>
            📌 <strong>Instructor:</strong> {enrollment.studentNote.length>80?enrollment.studentNote.slice(0,80)+'…':enrollment.studentNote}
          </div>
        )}
        <Link to={`/dashboard/course/${enrollment._id}`} className="btn btn-primary" style={{width:'100%',justifyContent:'center',marginTop:'auto'}}>
          {totalItems>0?<><FiPlay size={14}/> {pct>0?'Continue Learning':'Start Learning'}</>:<><FiBook size={14}/> View Course</>}
          <FiArrowRight size={14}/>
        </Link>
      </div>
    </div>
  );
};

export default function DashboardPage() {
  const {user,updateUser} = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [applications,setApplications] = useState([]);
  const [payments,setPayments]         = useState([]);
  const [enrollments,setEnrollments]   = useState([]);
  const [liveClasses, setLiveClasses]  = useState([]);
  const [tab,setTab]                   = useState(searchParams.get('tab')||'overview');
  const [editMode,setEditMode]         = useState(false);
  const [profileForm,setProfileForm]   = useState({fullName:user?.fullName||'',phone:user?.phone||'',collegeName:user?.collegeName||'',yearOfStudy:user?.yearOfStudy||''});
  const [saving,setSaving]             = useState(false);

  useEffect(()=>{

    getMyApplications().then(r=>setApplications(r.data.applications||[])).catch(()=>{});
    getMyPayments().then(r=>setPayments(r.data.payments||[])).catch(()=>{});
    getMyEnrollments().then(r=>setEnrollments(r.data.enrollments||[])).catch(()=>{});
    getLiveClasses().then(r=>setLiveClasses(r.data.classes||[])).catch(()=>{});
  },[]);

  const handleProfileSave = async()=>{
    setSaving(true);
    try{ const res=await updateProfile(profileForm); updateUser(res.data.user); setEditMode(false); toast.success('Profile updated!'); }
    catch{ toast.error('Update failed.'); } finally{setSaving(false);}
  };

  const tabs=[
    {id:'overview',icon:<FiTrendingUp size={15}/>,label:'Overview'},
    {id:'lms',icon:<FiPlay size={15}/>,label:'My Courses',count:enrollments.length},
    {id:'live', icon:<FiVideo size={15}/>, label:'Live Classes', count:liveClasses.filter(c=>c.status==='live').length||undefined},
    {id:'applications',icon:<FiCheckCircle size={15}/>,label:'Applications',count:applications.length},
    {id:'payments',icon:<FiCreditCard size={15}/>,label:'Payments'},
    {id:'profile',icon:<FiUser size={15}/>,label:'Profile'},
  ];

  return (
    <div style={{paddingTop:70,background:'var(--bg)',minHeight:'100vh'}}>
      <style>{`
        .dash-header{background:linear-gradient(135deg,#0f172a,#1a3a5c);padding:36px 0 0;}
        .dash-header-inner{max-width:1180px;margin:0 auto;padding:0 24px;}
        .dash-welcome-row{display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:16px;margin-bottom:24px;}
        .dash-welcome h1{color:white;font-size:24px;margin-bottom:4px;}
        .dash-welcome p{color:#94a3b8;font-size:14px;}
        .dash-header-stats{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:0;}
        .dash-stat-mini{background:rgba(255,255,255,0.07);border:1px solid rgba(255,255,255,0.1);border-radius:var(--radius-lg);padding:14px 18px;}
        .dash-stat-mini-value{font-size:22px;font-weight:800;color:white;font-family:var(--font-display);}
        .dash-stat-mini-label{font-size:11px;color:#94a3b8;margin-top:2px;}
        .dash-tabs-wrap{background:white;border-bottom:1px solid var(--border);}
        .dash-tabs{max-width:1180px;margin:0 auto;padding:0 24px;display:flex;gap:2px;overflow-x:auto;}
        .dash-tab{padding:14px 18px;font-size:14px;font-weight:600;color:var(--mid);cursor:pointer;border:none;background:none;white-space:nowrap;font-family:var(--font-display);display:flex;align-items:center;gap:7px;transition:var(--transition);border-bottom:3px solid transparent;margin-bottom:-1px;}
        .dash-tab.active{color:var(--primary);border-bottom-color:var(--primary);}
        .dash-tab:hover:not(.active){color:var(--dark);}
        .dash-tab-count{background:var(--primary-light);color:var(--primary);padding:1px 7px;border-radius:999px;font-size:11px;font-weight:700;}
        .dash-tab.active .dash-tab-count{background:var(--primary);color:white;}
        .dash-body{max-width:1180px;margin:0 auto;padding:28px 24px;}
        .dash-card{background:white;border-radius:var(--radius-xl);box-shadow:var(--shadow-sm);border:1px solid var(--border);overflow:hidden;margin-bottom:20px;}
        .dash-card-header{padding:18px 22px;border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;}
        .dash-card-header h3{font-size:16px;margin:0;}
        .dash-card-body{padding:20px 22px;}
        .app-row{display:flex;align-items:center;gap:14px;padding:14px 16px;border:1px solid var(--border);border-radius:var(--radius-lg);margin-bottom:10px;background:var(--bg);transition:var(--transition);}
        .app-row:last-child{margin-bottom:0;}
        .app-row:hover{border-color:var(--primary);background:var(--primary-light);}
        .app-course-icon{width:42px;height:42px;border-radius:10px;background:linear-gradient(135deg,var(--primary),var(--accent));display:flex;align-items:center;justify-content:center;font-size:20px;flex-shrink:0;}
        .pay-row{display:flex;align-items:center;gap:14px;padding:14px 16px;border:1px solid var(--border);border-radius:var(--radius-lg);margin-bottom:10px;background:var(--bg);}
        .pay-row:last-child{margin-bottom:0;}
        .lms-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:20px;}
        .profile-grid{display:grid;grid-template-columns:1fr 1fr;gap:20px;}
        .profile-label{font-size:12px;color:var(--mid);font-weight:500;margin-bottom:3px;text-transform:uppercase;letter-spacing:0.4px;}
        .profile-value{font-size:15px;font-weight:500;color:var(--dark);}
        @media(max-width:900px){.dash-header-stats{grid-template-columns:repeat(2,1fr);}.lms-grid{grid-template-columns:1fr;}.profile-grid{grid-template-columns:1fr;}}
      `}</style>

      <div className="dash-header">
        <div className="dash-header-inner">
          <div className="dash-welcome-row">
            <div className="dash-welcome">
              <h1>👋 Hello, {user?.fullName?.split(' ')[0]}!</h1>
              <p>Welcome to your learning dashboard</p>
            </div>
            <Link to="/apply" className="btn btn-accent btn-sm">+ Apply for a Course</Link>
          </div>
          <div className="dash-header-stats">
            {[
              {label:'Enrolled Courses',value:enrollments.length,icon:'🎓'},
              {label:'Applications',value:applications.length,icon:'📋'},
              {label:'Items Completed',value:enrollments.reduce((s,e)=>s+(e.completedItems?.length||0),0),icon:'✅'},
              {label:'Total Paid',value:`₹${(payments.reduce((s,p)=>s+(p.status==='paid'?p.amount/100:0),0)).toLocaleString()}`,icon:'💰'},
            ].map((s,i)=>(
              <div key={i} className="dash-stat-mini">
                <div className="dash-stat-mini-value">{s.value}</div>
                <div className="dash-stat-mini-label">{s.icon} {s.label}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="dash-tabs-wrap" style={{marginTop:16}}>
          <div className="dash-tabs">
            {tabs.map(t=>(
              <button key={t.id} className={`dash-tab${tab===t.id?' active':''}`} onClick={()=>setTab(t.id)}>
                {t.icon} {t.label}
                {t.count>0&&<span className="dash-tab-count">{t.count}</span>}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="dash-body">

        {/* OVERVIEW */}
        {tab==='overview'&&(
          <div>
            {enrollments.length===0&&applications.length===0?(
              <div className="dash-card"><div className="dash-card-body" style={{textAlign:'center',padding:'56px 24px'}}>
                <div style={{fontSize:56,marginBottom:16}}>🎓</div>
                <h3 style={{marginBottom:8}}>Start Your Learning Journey</h3>
                <p style={{color:'var(--mid)',maxWidth:380,margin:'0 auto 24px',fontSize:14,lineHeight:1.7}}>Apply to one of our industry-ready programs. After approval, your courses appear here with videos, files, assignments, and more.</p>
                <Link to="/apply" className="btn btn-accent btn-lg">Apply Now — Free</Link>
              </div></div>
            ):(
              <div>
                {enrollments.length>0&&(
                  <div className="dash-card" style={{marginBottom:20}}>
                    <div className="dash-card-header"><h3>📚 My Active Courses</h3><button className="btn btn-outline btn-sm" onClick={()=>setTab('lms')}>View All <FiArrowRight size={13}/></button></div>
                    <div className="dash-card-body" style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:16}}>
                      {enrollments.slice(0,3).map(e=><EnrolledCourseCard key={e._id} enrollment={e}/>)}
                    </div>
                  </div>
                )}
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20}}>
                  {applications.length>0&&(
                    <div className="dash-card" style={{marginBottom:0}}>
                      <div className="dash-card-header"><h3>Recent Applications</h3><button className="btn btn-outline btn-sm" onClick={()=>setTab('applications')}>View All</button></div>
                      <div className="dash-card-body">
                        {applications.slice(0,3).map((a,i)=>(
                          <div key={i} className="app-row">
                            <div className="app-course-icon">{a.course?.title?.includes('Full Stack')?'💻':a.course?.title?.includes('Data')?'🤖':'📱'}</div>
                            <div style={{flex:1}}><div style={{fontWeight:600,fontSize:14}}>{a.course?.title||'Course'}</div><div style={{fontSize:12,color:'var(--mid)'}}>#{a.applicationNumber}</div></div>
                            {statusBadge(a.status)}
                            {a.status==='approved'&&<Link to={`/payment/${a.course?._id}?applicationId=${a._id}`} className="btn btn-accent btn-sm">Pay</Link>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {payments.length>0&&(
                    <div className="dash-card" style={{marginBottom:0}}>
                      <div className="dash-card-header"><h3>Recent Payments</h3><button className="btn btn-outline btn-sm" onClick={()=>setTab('payments')}>View All</button></div>
                      <div className="dash-card-body">
                        {payments.slice(0,3).map((p,i)=>(
                          <div key={i} className="pay-row">
                            <div style={{flex:1}}><div style={{fontWeight:600,fontSize:14}}>{p.courseId?.title}</div><div style={{fontSize:12,color:'var(--mid)'}}>{p.invoiceNumber}</div></div>
                            <div style={{fontWeight:800,color:'var(--success)',fontFamily:'var(--font-display)'}}>₹{(p.amount/100).toLocaleString()}</div>
                            {payBadge(p.status)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* MY COURSES */}
        {tab==='lms'&&(
          <div>
            {enrollments.length===0?(
              <div className="dash-card"><div className="dash-card-body" style={{textAlign:'center',padding:'56px 24px'}}>
                <div style={{fontSize:56,marginBottom:16}}>📚</div>
                <h3 style={{marginBottom:8}}>No Courses Yet</h3>
                <p style={{color:'var(--mid)',maxWidth:400,margin:'0 auto',fontSize:14,lineHeight:1.7,marginBottom:16}}>
                  Once your application is <strong>approved</strong> by admin and you are enrolled, your course content (videos, files, assignments) will appear here.
                </p>
                {applications.filter(a=>['pending','under_review'].includes(a.status)).length>0&&(
                  <div style={{background:'var(--primary-light)',border:'1px solid #93c5fd',borderRadius:'var(--radius-lg)',padding:'14px 20px',marginBottom:20,maxWidth:440,margin:'0 auto 20px'}}>
                    <div style={{fontWeight:700,color:'var(--primary)',marginBottom:4}}>⏳ Application Under Review</div>
                    <div style={{fontSize:13,color:'var(--mid)'}}>We'll notify you within 24–48 hours.</div>
                  </div>
                )}
                <div style={{display:'flex',gap:12,justifyContent:'center',flexWrap:'wrap',marginTop:16}}>
                  <button className="btn btn-outline" onClick={()=>setTab('applications')}>Check Applications</button>
                  <Link to="/apply" className="btn btn-accent">Apply for a Course</Link>
                </div>
              </div></div>
            ):(
              <>
                <div style={{marginBottom:20}}>
                  <h2 style={{fontSize:20,marginBottom:4}}>My Enrolled Courses</h2>
                  <p style={{color:'var(--mid)',fontSize:14}}>{enrollments.length} course{enrollments.length>1?'s':''} · Continue where you left off</p>
                </div>
                <div className="lms-grid">
                  {enrollments.map(e=><EnrolledCourseCard key={e._id} enrollment={e}/>)}
                </div>
                {enrollments.some(e=>e.progressPercent===100)&&(
                  <div style={{marginTop:28,background:'linear-gradient(135deg,#f0fdf4,#d1fae5)',border:'1px solid #6ee7b7',borderRadius:'var(--radius-xl)',padding:'24px',textAlign:'center'}}>
                    <FiAward size={32} color="var(--success)"/>
                    <h3 style={{marginTop:10,color:'var(--success)'}}>🎉 Course Completed!</h3>
                    <p style={{color:'var(--mid)',fontSize:14}}>You've completed a course. Your certificate will be available soon.</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* APPLICATIONS */}
        {tab==='applications'&&(
          <div className="dash-card">
            <div className="dash-card-header"><h3>My Applications ({applications.length})</h3><Link to="/apply" className="btn btn-accent btn-sm">+ Apply Again</Link></div>
            <div className="dash-card-body">
              {applications.length===0?(
                <div style={{textAlign:'center',padding:'36px 24px',color:'var(--mid)'}}><p style={{marginBottom:16}}>No applications yet.</p><Link to="/apply" className="btn btn-primary">Apply Now</Link></div>
              ):applications.map((a,i)=>(
                <div key={i} className="app-row">
                  <div className="app-course-icon">{a.course?.title?.includes('Full Stack')?'💻':a.course?.title?.includes('Data')?'🤖':'📱'}</div>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:600,fontSize:15}}>{a.course?.title||'Course'}</div>
                    <div style={{fontSize:12,color:'var(--mid)'}}>#{a.applicationNumber} · {new Date(a.createdAt).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})} · ₹{a.course?.fee?.total?.toLocaleString()}</div>
                  </div>
                  <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:8}}>
                    {statusBadge(a.status)}
                    {a.status==='approved'&&<Link to={`/payment/${a.course?._id}?applicationId=${a._id}`} className="btn btn-accent btn-sm">💳 Pay Registration Fee</Link>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* PAYMENTS */}
        {tab==='payments'&&(
          <div className="dash-card">
            <div className="dash-card-header"><h3>Payment History</h3></div>
            <div className="dash-card-body">
              {payments.length===0?<div style={{textAlign:'center',padding:'36px 24px',color:'var(--mid)'}}>No payments yet.</div>:payments.map((p,i)=>(
                <div key={i} className="pay-row">
                  <div style={{flex:1}}>
                    <div style={{fontWeight:600,fontSize:15}}>{p.courseId?.title}</div>
                    <div style={{fontSize:12,color:'var(--mid)'}}>{p.invoiceNumber} · {new Date(p.paidAt).toLocaleDateString('en-IN')} · <span style={{textTransform:'capitalize'}}>{p.type}</span></div>
                  </div>
                  <div style={{fontSize:18,fontWeight:800,color:'var(--success)',fontFamily:'var(--font-display)'}}>₹{(p.amount/100).toLocaleString()}</div>
                  {payBadge(p.status)}
                  <a href={`/api/payments/receipt/${p._id}`} target="_blank" rel="noreferrer" className="btn btn-outline btn-sm"><FiDownload size={13}/> Receipt</a>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* LIVE CLASSES */}
        {tab==='live'&&(
          <div>
            {liveClasses.filter(c=>c.status==='live').length > 0 && (
              <div className="dash-card" style={{ borderColor:'#16a34a', borderWidth:2, marginBottom:18 }}>
                <div className="dash-card-header" style={{ background:'#f0fdf4' }}>
                  <h3 style={{ color:'#16a34a', display:'flex', alignItems:'center', gap:8 }}>
                    <span style={{ width:10,height:10,borderRadius:'50%',background:'#ef4444',display:'inline-block' }} />
                    Live Right Now!
                  </h3>
                </div>
                <div className="dash-card-body">
                  {liveClasses.filter(c=>c.status==='live').map(cls=>(
                    <div key={cls._id} style={{ background:'#f0fdf4',border:'1px solid #6ee7b7',borderRadius:12,padding:'16px 20px',marginBottom:10,display:'flex',alignItems:'center',gap:16,flexWrap:'wrap' }}>
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:700,fontSize:16,marginBottom:4 }}>{cls.title}</div>
                        <div style={{ fontSize:13,color:'var(--mid)' }}>
                          {cls.subject} · Teacher: {cls.teacher?.fullName}
                          {cls.course && ' · ' + cls.course.title}
                        </div>
                      </div>
                      <a href={cls.meetLink} target="_blank" rel="noreferrer" className="btn btn-primary" style={{ background:'#16a34a',borderColor:'#16a34a' }}>
  <FiVideo size={14}/> Join Live Class
</a>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="dash-card">
              <div className="dash-card-header">
                <h3>All Live Classes</h3>
              </div>
              <div className="dash-card-body">
                {liveClasses.length === 0 ? (
                  <div style={{ textAlign:'center',padding:'40px 24px',color:'var(--mid)' }}>
                    <div style={{ fontSize:48,marginBottom:14 }}>📺</div>
                    <h3 style={{ marginBottom:8,color:'var(--dark)' }}>No Live Classes Yet</h3>
                    <p style={{ fontSize:14 }}>Your teacher will schedule live classes. You will see them here.</p>
                  </div>
                ) : liveClasses.map(cls=>{
                  const statusStyle = {
                    scheduled:{ bg:'#f0f4ff',color:'#2563eb',label:'📅 Scheduled' },
                    live:     { bg:'#f0fdf4',color:'#16a34a',label:'🔴 LIVE NOW' },
                    ended:    { bg:'#f8fafc',color:'#64748b',label:'✅ Ended' },
                    cancelled:{ bg:'#fef2f2',color:'#dc2626',label:'❌ Cancelled' },
                  }[cls.status] || { bg:'#f0f4ff',color:'#2563eb',label:cls.status };
                  const replayReady = cls.replayUrl && cls.replayAvailableAt && new Date()>=new Date(cls.replayAvailableAt);
                  return(
                    <div key={cls._id} style={{ border:'1px solid var(--border)',borderRadius:12,padding:'16px 20px',marginBottom:10,display:'flex',alignItems:'center',gap:14,flexWrap:'wrap' }}>
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:700,fontSize:15,marginBottom:4 }}>{cls.title}</div>
                        <div style={{ fontSize:13,color:'var(--mid)',marginBottom:6 }}>
                          {cls.subject}
                          {cls.teacher && ' · 👨‍🏫 '+cls.teacher.fullName}
                          {cls.course  && ' · 📚 '+cls.course.title}
                        </div>
                        <div style={{ fontSize:12,color:'var(--mid)' }}>
                          {new Date(cls.scheduledAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' })} at {new Date(cls.scheduledAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })} · ⏱ {cls.duration} min
                        </div>
                      </div>
                      <span style={{ background:statusStyle.bg,color:statusStyle.color,padding:'4px 12px',borderRadius:999,fontSize:12,fontWeight:700,flexShrink:0 }}>
                        {statusStyle.label}
                      </span>
                      {cls.status==='live' && (
  <a href={cls.meetLink} target="_blank" rel="noreferrer" className="btn btn-primary btn-sm" style={{ background:'#16a34a',borderColor:'#16a34a',flexShrink:0 }}>
    <FiVideo size={13}/> Join Now
  </a>
)}
                      {cls.status==='ended' && replayReady && (
                        <Link to={'/live-class/'+cls._id} className="btn btn-outline btn-sm" style={{ flexShrink:0 }}>
                          ▶ Watch Replay
                        </Link>
                      )}
                      {cls.status==='ended' && !replayReady && cls.replayUrl && (
                        <span style={{ fontSize:12,color:'var(--mid)',flexShrink:0 }}>Replay available soon</span>
                      )}
                      {cls.status==='scheduled' && (
                        <Link to={'/live-class/'+cls._id} className="btn btn-outline btn-sm" style={{ flexShrink:0 }}>
                          View Details
                        </Link>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* PROFILE */}
        {tab==='profile'&&(
          <div>
            <div className="dash-card">
              <div className="dash-card-header"><h3>My Profile</h3><button className="btn btn-outline btn-sm" onClick={()=>setEditMode(m=>!m)}><FiEdit2 size={13}/> {editMode?'Cancel':'Edit Profile'}</button></div>
              <div className="dash-card-body">
                {editMode?(
                  <div>
                    <div className="form-grid">
                      <div className="form-group"><label>Full Name</label><input value={profileForm.fullName} onChange={e=>setProfileForm(f=>({...f,fullName:e.target.value}))} className="form-control"/></div>
                      <div className="form-group"><label>Phone Number</label><input value={profileForm.phone} onChange={e=>setProfileForm(f=>({...f,phone:e.target.value}))} className="form-control" placeholder="+91 98765 43210"/></div>
                      <div className="form-group"><label>College / University</label><input value={profileForm.collegeName} onChange={e=>setProfileForm(f=>({...f,collegeName:e.target.value}))} className="form-control"/></div>
                      <div className="form-group"><label>Year of Study</label>
                        <select value={profileForm.yearOfStudy} onChange={e=>setProfileForm(f=>({...f,yearOfStudy:e.target.value}))} className="form-control">
                          <option value="">Select</option>
                          {['1st Year','2nd Year','3rd Year','4th Year','Graduate','Other'].map(y=><option key={y}>{y}</option>)}
                        </select>
                      </div>
                    </div>
                    <button className="btn btn-primary" onClick={handleProfileSave} disabled={saving}>{saving?'Saving…':'✓ Save Changes'}</button>
                  </div>
                ):(
                  <div className="profile-grid">
                    {[['Full Name',user?.fullName],['Email',user?.email],['Phone',user?.phone||'—'],['College',user?.collegeName||'—'],['Year',user?.yearOfStudy||'—'],['Account',user?.role==='admin'?'🔐 Admin':'🎓 Student']].map(([l,v])=>(
                      <div key={l}><div className="profile-label">{l}</div><div className="profile-value">{v}</div></div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="dash-card">
              <div className="dash-card-header"><h3>Quick Actions</h3></div>
              <div className="dash-card-body" style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:12}}>
                {[{icon:'📚',label:'View My Courses',action:()=>setTab('lms')},{icon:'📋',label:'My Applications',action:()=>setTab('applications')},{icon:'💳',label:'Payment History',action:()=>setTab('payments')},{icon:'🎓',label:'Apply for Course',action:()=>navigate('/apply')}].map((a,i)=>(
                  <button key={i} onClick={a.action} style={{background:'var(--bg)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',padding:'14px 16px',cursor:'pointer',display:'flex',alignItems:'center',gap:10,fontSize:14,fontWeight:500,color:'var(--dark)',transition:'var(--transition)',textAlign:'left'}}
                    onMouseOver={e=>{e.currentTarget.style.borderColor='var(--primary)';e.currentTarget.style.background='var(--primary-light)';}}
                    onMouseOut={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.background='var(--bg)';}}>
                    <span style={{fontSize:22}}>{a.icon}</span>{a.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}